import type { AxiosResponse } from 'axios';

/**
 * Normalize a URL into an absolute URL object.
 * Supports both absolute and relative inputs; returns null when parsing fails.
 */
export function toAbsoluteUrl(url: string, baseUrl?: string): URL | null {
  try {
    return new URL(url);
  } catch {
    if (!baseUrl) return null;
    try {
      return new URL(url, baseUrl);
    } catch {
      return null;
    }
  }
}

/**
 * Prevent open redirects by allowing only same-origin sign-in targets.
 * Falls back to the known sign-in route for any unsafe or invalid target.
 */
export function resolveSafeSignInTarget(
  url: string | undefined,
  current: URL,
  defaultSignInPath: string,
): URL {
  const fallback = toAbsoluteUrl(defaultSignInPath, current.href) || current;
  if (!url) return fallback;

  const candidate = toAbsoluteUrl(url, current.href);
  if (!candidate) return fallback;
  if (candidate.origin !== current.origin) return fallback;
  if (candidate.pathname !== fallback.pathname) return fallback;

  return candidate;
}

/** Same-origin sign-in page check (path + origin), for Inertia location headers. */
export function isSignInLocationUrl(
  url: string | undefined,
  defaultSignInPath: string,
  currentHref: string | undefined,
): boolean {
  if (!url || !currentHref) return false;

  const candidate = toAbsoluteUrl(url, currentHref);
  const signIn = toAbsoluteUrl(defaultSignInPath, currentHref);

  if (!candidate || !signIn) return false;

  return candidate.origin === signIn.origin && candidate.pathname === signIn.pathname;
}

/**
 * Detect the common stale-session case where XHR is silently redirected
 * to the login HTML page instead of returning JSON payloads.
 */
export function isStaleSessionHtmlResponse(
  response: Pick<AxiosResponse, 'headers' | 'request'>,
  ctx: { documentHref: string; defaultSignInPath: string },
): boolean {
  const contentTypeHeader = response.headers?.['content-type'];
  const contentType = typeof contentTypeHeader === 'string' ? contentTypeHeader : '';
  if (!contentType.includes('text/html')) return false;

  const responseUrl = response.request?.responseURL;
  if (!responseUrl) return false;

  const parsed = toAbsoluteUrl(responseUrl, ctx.documentHref);
  if (!parsed) return false;
  return parsed.pathname === ctx.defaultSignInPath;
}

export type StaleSessionAxiosError = {
  response?: { status?: number; headers?: Record<string, unknown> };
};

/**
 * Whether axios error handling should treat this as a stale session and redirect to sign-in.
 * Caller passes the URL string later given to `redirectToSignIn` (Inertia location or undefined).
 */
export function shouldRedirectStaleSessionOnAxiosError(
  error: StaleSessionAxiosError,
  ctx: { currentHref: string; defaultSignInPath: string },
): boolean {
  const status = error.response?.status as number | undefined;
  const raw = error.response?.headers?.['x-inertia-location'];
  const inertiaLocation = typeof raw === 'string' ? raw : undefined;

  return (
    status === 401 ||
    status === 419 ||
    (status === 409 && isSignInLocationUrl(inertiaLocation, ctx.defaultSignInPath, ctx.currentHref))
  );
}
